import {
  registerDecorator,
  type ValidationOptions,
  type ValidationArguments,
  validateSync,
  type ValidationError,
} from 'class-validator';
import { plainToInstance } from 'class-transformer';
import { formatValidationErrors } from '@builder4/shared/utils/validators';
import { getPropsDto } from '../props/registry';

// Store validation errors keyed by the parent object for error message formatting
const validationErrorsMap = new WeakMap<
  object,
  { errors: ValidationError[]; displayName: string }
>();

/**
 * Validates node props based on the displayName field of the parent SerializedNodeDto.
 *
 * - If displayName maps to a registered DTO, validates props against that DTO
 * - If no DTO is registered for the displayName, performs basic object validation (fallback)
 * - Provides detailed error messages showing which property failed validation
 */
export function ValidateNodeProps(validationOptions?: ValidationOptions) {
  return function (object: object, propertyName: string) {
    registerDecorator({
      name: 'validateNodeProps',
      target: object.constructor,
      propertyName: propertyName,
      options: validationOptions,
      validator: {
        validate(value: unknown, args: ValidationArguments) {
          // Basic object check - props must be a non-null, non-array object
          if (typeof value !== 'object' || value === null || Array.isArray(value)) {
            return false;
          }

          const parentObject = args.object as { displayName?: string };
          const displayName = parentObject.displayName;

          // No displayName means we can't determine which DTO to use
          // Just ensure props is an object (basic fallback)
          if (!displayName) {
            return true;
          }

          const DtoClass = getPropsDto(displayName);

          // No registered DTO for this displayName - use fallback (accept any object)
          if (!DtoClass) {
            return true;
          }

          // Transform and validate against the specific DTO
          const dtoInstance = plainToInstance(DtoClass, value, {
            enableImplicitConversion: true,
          });

          const errors = validateSync(dtoInstance, {
            skipMissingProperties: false,
            whitelist: false,
            forbidNonWhitelisted: false,
          });

          if (errors.length > 0) {
            // Store errors for message formatting using WeakMap keyed by parent object
            validationErrorsMap.set(args.object, { errors, displayName });
            return false;
          }

          return true;
        },
        defaultMessage(args: ValidationArguments) {
          const stored = validationErrorsMap.get(args.object);

          if (stored && stored.errors.length > 0) {
            const errorMessages = formatValidationErrors(stored.errors);
            validationErrorsMap.delete(args.object);
            return `Validation failed for ${stored.displayName} node:\n${errorMessages.join('\n')}`;
          }

          return `${args.property} must be a valid object`;
        },
      },
    });
  };
}
